// A business's own public link: a clean, lowercase, hyphenated URL slug
// such as yatusabespr.com/bebos-barbecue, shared from the detail card and QR code.
//
// An editor can set the link by hand instead (the Listing's link_tag, behind
// the admin's Page Link field) and that address is the one the site uses from
// then on. The name still resolves, so links handed out earlier keep working.
//
// Public business URLs use lowercase, ASCII-friendly hyphenated slugs.
// The resolver still matches legacy name-based URLs so older links can redirect.

/**
 * The readable form of a name, used for display/admin input.
 */
export const readableName = (value) =>
  String(value ?? "")
    .replace(/[’‘`´]/g, "'")
    .replace(/[/\\?#%<>:"|*]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * The comparison key used to find a business from a link. Case, accents,
 * punctuation and spacing don't matter, so an older dashed link (/bebos-bbq)
 * still lands on the same business.
 */
export const matchKey = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "");

/**
 * The name a business answers to: the link an editor set for it by hand, its
 * own name otherwise, and the stored slug for a record that has nothing else.
 */
export const listingName = (listing) =>
  readableName(listing?.link_tag) ||
  readableName(listing?.name) ||
  readableName(listing?.slug) ||
  String(listing?.id ?? "");

export const slugifyBusinessName = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[’‘`´']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const listingPath = (listing) => {
  const slug = slugifyBusinessName(listingName(listing));
  return slug ? `/${slug}` : `/${encodeURIComponent(String(listing?.id ?? ""))}`;
};

/** The absolute public link, using the canonical clean slug. */
export const businessLink = (listing, origin = window.location.origin) =>
  `${origin}${encodeURI(listingPath(listing))}`;

/**
 * What an editor typed in the admin's page-link field, reduced to the tag that
 * follows the site's address: a whole link pasted in keeps only its last part,
 * and characters that would break a URL are dropped the way a name's are.
 */
export const linkFromInput = (value) => {
  const raw = String(value ?? "").trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
  const parts = raw.split(/[/?#]+/).filter(Boolean);
  return readableName(parts.length > 1 ? parts[parts.length - 1] : raw);
};